import { DECISION_METHODS, datePl, OFFER_DELIVERY_METHODS, type OfferVersionDto, REJECTION_REASONS, todayInWarsaw } from '@bqcrm/shared';
import { Alert, Button, Group, Modal, Select, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { IconEdit, IconSend } from '@tabler/icons-react';
import { useState } from 'react';
import { api } from '../../api';
import { formatDateTime } from '../../format';

export type DecisionDialog = 'delivery' | 'reject' | 'changes';
type Run = (fn: () => Promise<OfferVersionDto>) => void;

const today = () => todayInWarsaw();
const opts = (o: Record<string, string>) => Object.entries(o).map(([value, label]) => ({ value, label }));

/** Okna decyzji klienta zanotowanych przez biuro: przekazanie oferty, odrzucenie, prośba o zmiany (docs/10 §3.3). */
export function OfferDecisionModals({ v, dialog, onClose, run, busy }: { v: OfferVersionDto; dialog: DecisionDialog | null; onClose: () => void; run: Run; busy: boolean }) {
  const [delivery, setDelivery] = useState({ method: 'osobiscie', date: today(), note: '' });
  const [rej, setRej] = useState({ personName: '', reasonCode: 'cena', reason: '', method: 'telefon' });
  const [note, setNote] = useState('');
  const officeMethods = Object.fromEntries(Object.entries(DECISION_METHODS).filter(([k]) => k !== 'link'));
  return (
    <>
      <Modal opened={dialog === 'delivery'} onClose={onClose} title="Przekazanie oferty klientowi">
        <Stack>
          <Text size="sm" c="dimmed">
            Wydanie zamroziło treść oferty; tu zapisujesz, kiedy i jak klient ją otrzymał. Udostępnienie linkiem zapisuje się samo.
          </Text>
          <Select label="Sposób" data={opts(OFFER_DELIVERY_METHODS)} value={delivery.method} allowDeselect={false} onChange={(x) => x && setDelivery({ ...delivery, method: x })} />
          <TextInput type="date" label="Data" max={today()} value={delivery.date} onChange={(e) => setDelivery({ ...delivery, date: e.currentTarget.value })} />
          <TextInput label="Uwagi" placeholder="np. wręczona na spotkaniu w domu klienta" value={delivery.note} onChange={(e) => setDelivery({ ...delivery, note: e.currentTarget.value })} />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Anuluj
            </Button>
            <Button loading={busy} onClick={() => run(() => api<OfferVersionDto>(`/offer-versions/${v.id}/delivery`, { method: 'POST', body: delivery }))}>
              Zapisz przekazanie
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={dialog === 'reject'} onClose={onClose} title="Klient odrzucił ofertę">
        <Stack>
          <Text size="sm" c="dimmed">
            Wersji nie da się potem zaakceptować. Dalej przygotujesz nową wersję oferty albo oznaczysz zakres jako przegrany.
          </Text>
          <TextInput label="Kto przekazał decyzję" required value={rej.personName} onChange={(e) => setRej({ ...rej, personName: e.currentTarget.value })} data-autofocus />
          <Group grow>
            <Select label="Powód" data={opts(REJECTION_REASONS)} value={rej.reasonCode} allowDeselect={false} onChange={(x) => x && setRej({ ...rej, reasonCode: x })} />
            <Select label="Jak" data={opts(officeMethods)} value={rej.method} allowDeselect={false} onChange={(x) => x && setRej({ ...rej, method: x })} />
          </Group>
          <Textarea label="Szczegóły" autosize minRows={2} value={rej.reason} onChange={(e) => setRej({ ...rej, reason: e.currentTarget.value })} />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Anuluj
            </Button>
            <Button color="orange" disabled={rej.personName.trim().length < 2} loading={busy} onClick={() => run(() => api<OfferVersionDto>(`/offer-versions/${v.id}/rejection`, { method: 'POST', body: rej }))}>
              Zapisz odrzucenie
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={dialog === 'changes'} onClose={onClose} title="Klient prosi o zmiany">
        <Stack>
          <Text size="sm" c="dimmed">
            Wersja dostanie stan „Klient prosi o zmiany” (nie da się jej zaakceptować), a w sprawie pojawi się zadanie przygotowania nowej wersji oferty. Notatka będzie widoczna przy tej wersji.
          </Text>
          <Textarea label="Czego oczekuje klient" required autosize minRows={3} value={note} onChange={(e) => setNote(e.currentTarget.value)} data-autofocus />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Anuluj
            </Button>
            <Button disabled={note.trim().length < 3} loading={busy} onClick={() => run(() => api<OfferVersionDto>(`/offer-versions/${v.id}/changes`, { method: 'POST', body: { note } }))}>
              Zapisz prośbę o zmiany
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

/** Informacje przy wersji: przekazanie klientowi, prośba o zmiany. */
export function OfferDecisionInfo({ v }: { v: OfferVersionDto }) {
  return (
    <>
      {v.changes && (
        <Alert color="yellow" mb="md" icon={<IconEdit size={18} />} title="Klient prosi o zmiany">
          {v.changes.note}
          <Text size="xs" c="dimmed" mt={4}>
            {v.changes.byName ?? '—'}, {formatDateTime(v.changes.at)} · przygotuj nową wersję (menu ⋯ → Nowa wersja)
          </Text>
        </Alert>
      )}
      {v.delivery && (
        <Text size="sm" c="dimmed" mb="md">
          <IconSend size={13} style={{ verticalAlign: -2 }} /> Przekazana klientowi: {v.delivery.methodLabel.toLowerCase()}, {datePl(v.delivery.at)}
          {v.delivery.note ? ` — ${v.delivery.note}` : ''}
          {v.delivery.byName ? ` · ${v.delivery.byName}` : ''}
        </Text>
      )}
    </>
  );
}
